import React, { useState, useEffect } from 'react';
import { Bell, LogOut, User as UserIcon, X, CheckCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  read: boolean;
  createdAt: Date;
}

interface NotificationsPanelProps {
  notifications: Notification[];
  onClose: () => void;
  onMarkAsRead: (id: string) => void;
  onMarkAllAsRead: () => void;
}

const NotificationsPanel: React.FC<NotificationsPanelProps> = ({ 
  notifications, 
  onClose, 
  onMarkAsRead,
  onMarkAllAsRead 
}) => {
  const getNotificationStyle = (type: Notification['type']) => {
    switch (type) {
      case 'success':
        return 'bg-green-50 text-green-800';
      case 'warning':
        return 'bg-yellow-50 text-yellow-800';
      case 'error':
        return 'bg-red-50 text-red-800';
      default:
        return 'bg-blue-50 text-blue-800';
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />
      <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex">
        <div className="w-screen max-w-md">
          <div className="h-full flex flex-col bg-white shadow-xl">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-medium text-gray-900">Notificações</h2>
                <div className="flex items-center space-x-4">
                  <button
                    onClick={onMarkAllAsRead}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    Marcar todas como lidas
                  </button>
                  <button
                    onClick={onClose}
                    className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-4">
                {notifications.length === 0 ? (
                  <div className="text-center text-gray-500 py-8">
                    Nenhuma notificação no momento
                  </div>
                ) : (
                  notifications.map((notification) => (
                    <div
                      key={notification.id}
                      className={`p-4 rounded-lg ${getNotificationStyle(notification.type)} ${
                        !notification.read ? 'border-l-4 border-current' : ''
                      }`}
                      onClick={() => onMarkAsRead(notification.id)}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h3 className="text-sm font-medium">
                            {notification.title}
                          </h3>
                          <p className="mt-1 text-sm opacity-90">
                            {notification.message}
                          </p>
                          <p className="mt-2 text-xs opacity-75">
                            {new Date(notification.createdAt).toLocaleString('pt-BR')}
                          </p>
                        </div>
                        {!notification.read && (
                          <span className="ml-4 text-xs font-medium opacity-75">
                            Não lida
                          </span>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

interface ProfilePanelProps {
  onClose: () => void;
  firstName: string;
}

const ProfilePanel: React.FC<ProfilePanelProps> = ({ onClose, firstName }) => {
  const { user } = useAuth();
  const userMetadata = user?.user_metadata;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />
      <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex">
        <div className="w-screen max-w-md">
          <div className="h-full flex flex-col bg-white shadow-xl">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-medium text-gray-900">Perfil do Usuário</h2>
                <button
                  onClick={onClose}
                  className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-6">
                <div>
                  <div className="flex items-center space-x-4">
                    <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                      <UserIcon className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">
                        {userMetadata?.name || user?.email}
                      </h3>
                      <p className="text-sm text-gray-500">{user?.email}</p>
                    </div>
                  </div>
                </div>
                <div className="border-t border-gray-200 pt-6">
                  <dl className="space-y-4">
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Tipo de Usuário</dt>
                      <dd className="mt-1 text-sm text-gray-900">Administrador</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Último Acesso</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {new Date().toLocaleString('pt-BR')}
                      </dd>
                    </div>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const DashboardHeader: React.FC = () => {
  const { user, signOut } = useAuth();
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([
    {
      id: '1',
      title: 'Bem-vindo ao UPWISE!',
      message: 'Estamos felizes em tê-lo conosco. Explore todas as funcionalidades do sistema para otimizar a gestão do seu negócio.',
      type: 'success',
      read: false,
      createdAt: new Date()
    },
    {
      id: '2',
      title: 'Lembrete de Backup',
      message: 'Recomendamos fazer backup dos seus dados regularmente para garantir a segurança das informações.',
      type: 'info',
      read: false,
      createdAt: new Date()
    }
  ]);

  // Get first name from user metadata or email
  const getFirstName = () => {
    const fullName = user?.user_metadata?.name;
    if (fullName) {
      return fullName.split(' ')[0];
    }
    return user?.email?.split('@')[0] || 'Usuário';
  };

  const firstName = getFirstName();

  const unreadCount = notifications.filter(n => !n.read).length;

  const handleMarkAsRead = (id: string) => {
    setNotifications(notifications.map(notification =>
      notification.id === id ? { ...notification, read: true } : notification
    ));
  };

  const handleMarkAllAsRead = () => {
    setNotifications(notifications.map(notification => ({ ...notification, read: true })));
  };

  // Auto-hide notifications panel when all are read
  useEffect(() => {
    if (showNotifications && unreadCount === 0) {
      const timer = setTimeout(() => {
        setShowNotifications(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [unreadCount, showNotifications]);

  return (
    <header className="bg-white border-b border-gray-200 px-6 py-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <h1 className="text-2xl font-bold text-gray-900">
            Bem-vindo, {firstName}
          </h1>
          <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
            Administrador
          </span>
        </div>

        <div className="flex items-center space-x-4">
          <button 
            className="p-2 hover:bg-gray-100 rounded-full relative"
            onClick={() => setShowNotifications(true)}
          >
            <Bell className="h-6 w-6 text-gray-600" />
            {unreadCount > 0 && (
              <span className="absolute top-0 right-0 h-5 w-5 bg-red-500 rounded-full text-xs text-white flex items-center justify-center">
                {unreadCount}
              </span>
            )}
          </button>

          <button 
            className="p-2 hover:bg-gray-100 rounded-full"
            onClick={() => setShowProfile(true)}
          >
            <UserIcon className="h-6 w-6 text-gray-600" />
          </button>

          <button
            onClick={signOut}
            className="flex items-center space-x-2 px-4 py-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <LogOut className="h-5 w-5 text-gray-600" />
            <span className="text-gray-700">Sair</span>
          </button>
        </div>
      </div>

      {showNotifications && (
        <NotificationsPanel 
          notifications={notifications}
          onClose={() => setShowNotifications(false)}
          onMarkAsRead={handleMarkAsRead}
          onMarkAllAsRead={handleMarkAllAsRead}
        />
      )}
      {showProfile && (
        <ProfilePanel 
          onClose={() => setShowProfile(false)} 
          firstName={firstName} 
        />
      )}
    </header>
  );
};

export default DashboardHeader;